<script setup lang="ts">
import { onMounted, ref } from 'vue';
import Bugsnag from '@bugsnag/js';
import { storeToRefs } from 'pinia';
import useMeQuery from '@/api/queries/me/useMeQuery';
import { useLocaleStore } from '@/stores/locales';

const { currentLocale } = storeToRefs(useLocaleStore());
const { auth } = useMeQuery();

async function loadTypeformScript(): Promise<{ status: boolean; message?: string }> {
  return new Promise((resolve, reject) => {
    try {
      const script = document.createElement('script');
      script.src = `//embed.typeform.com/next/embed.js`;
      script.async = true;
      script.addEventListener('load', () => {
        resolve({ status: true });
      });

      script.addEventListener('error', (e) => {
        reject(e);
      });

      document.body.append(script);
    } catch (error) {
      reject(error);
    }
  });
}

onMounted(async () => {
  try {
    await loadTypeformScript();
  } catch (error) {
    Bugsnag.notify(error, (event) => {
      event.context = 'Failed to load external script - Typeform';
    });
  }
});

const displayBanner = ref(['fr-FR', 'en-US', 'en-GB'].includes(currentLocale.value.iso_code_639));

const typeformId = currentLocale.value.iso_code_639 == 'fr-FR' ? 'dGGGmo36' : 'oxXp66OD';
const typeformEmail = auth.value?.email;

const typeformFr = {
  text: 'Aide-nous à mieux comprendre ton expérience avec cette courte étude !',
  button: 'Je participe',
};

const typeformEn = {
  text: 'Help us understand your experience with this short study!',
  button: 'I’m in',
};
</script>

<template>
  <div
    v-if="displayBanner"
    class="flex flex-wrap text-center w-full bg-af-teal-600 text-white py-3 px-12 md:px-3 text-sm justify-center items-center gap-3 relative"
  >
    <p v-if="currentLocale.iso_code_639 === 'fr-FR'">{{ typeformFr.text }}</p>
    <p v-else>{{ typeformEn.text }}</p>
    <button
      :data-tf-popup="typeformId"
      data-tf-opacity="100"
      data-tf-size="100"
      data-tf-iframe-props="title=🧑‍🎓 Personal Account"
      :data-tf-hidden="'email=' + typeformEmail"
      data-tf-medium="snippet"
      class="tf-button bg-white py-2 px-4 font-medium text-af-teal-600 rounded-full"
    >
      <span v-if="currentLocale.iso_code_639 === 'fr-FR'">{{ typeformFr.button }}</span>
      <span v-else>{{ typeformEn.button }}</span>
    </button>

    <button type="button" @click="displayBanner = false" class="absolute end-3">
      <VIcon icon="fat fa-xmark" size="24px" />
    </button>
  </div>
</template>
